import { isValidBsDate } from './calendar.js';
import { BsInvalidDateError } from './errors.js';
const DEVANAGARI_DIGITS = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
/**
 * Month names as they appear in Nepali government documents (the formal/Sanskrit
 * forms). Colloquial spoken forms differ — see BS_MONTH_NAMES_NE_COLLOQUIAL. Which
 * set the portal renders is a translation-QA decision (A-020), not a code decision;
 * both are exported so changing it is a one-line change with no data migration.
 */
export const BS_MONTH_NAMES_NE = [
    'वैशाख',
    'जेठ',
    'असार',
    'श्रावण',
    'भाद्र',
    'आश्विन',
    'कार्तिक',
    'मंसिर',
    'पौष',
    'माघ',
    'फाल्गुन',
    'चैत्र',
];
/** The colloquial spoken forms, for contexts where the formal register reads stiff. */
export const BS_MONTH_NAMES_NE_COLLOQUIAL = [
    'बैशाख',
    'जेठ',
    'असार',
    'साउन',
    'भदौ',
    'असोज',
    'कात्तिक',
    'मंसिर',
    'पुस',
    'माघ',
    'फागुन',
    'चैत',
];
/** Romanised month names, as used in English-language Nepali government correspondence. */
export const BS_MONTH_NAMES_EN = [
    'Baishakh',
    'Jestha',
    'Ashadh',
    'Shrawan',
    'Bhadra',
    'Ashwin',
    'Kartik',
    'Mangsir',
    'Poush',
    'Magh',
    'Falgun',
    'Chaitra',
];
/** Latin digits -> Devanagari digits. Non-digits pass through untouched. */
export function toDevanagariNumerals(value) {
    return String(value).replace(/[0-9]/g, (digit) => DEVANAGARI_DIGITS[Number(digit)]);
}
/** Devanagari digits -> Latin digits. Non-digits pass through untouched. */
export function toLatinNumerals(value) {
    return value.replace(/[०-९]/g, (digit) => String(DEVANAGARI_DIGITS.indexOf(digit)));
}
/** Applies a numeral system to an already-rendered string. */
export function applyNumerals(value, numerals) {
    return numerals === 'devanagari' ? toDevanagariNumerals(value) : toLatinNumerals(value);
}
/** The month's name in the given locale. `month` is 1..12. */
export function bsMonthName(month, locale = 'en') {
    if (!Number.isInteger(month) || month < 1 || month > 12) {
        throw new BsInvalidDateError(`BS month must be 1..12, received ${month}`);
    }
    const names = locale === 'ne' ? BS_MONTH_NAMES_NE : BS_MONTH_NAMES_EN;
    return names[month - 1];
}
/**
 * Renders a BS date for display. Rendering only — nothing here is ever stored
 * (ADR-0008: Devanagari numerals are never stored, never parsed from storage).
 */
export function formatBsDate(date, options = {}) {
    if (!isValidBsDate(date)) {
        throw new BsInvalidDateError(`Cannot format BS ${JSON.stringify(date)}: not a valid date in the supported range`);
    }
    const locale = options.locale ?? 'en';
    const style = options.style ?? 'numeric';
    const numerals = options.numerals ?? (locale === 'ne' ? 'devanagari' : 'latin');
    const rendered = style === 'numeric'
        ? `${String(date.year).padStart(4, '0')}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`
        : `${date.day} ${bsMonthName(date.month, locale)} ${date.year}`;
    return applyNumerals(rendered, numerals);
}
//# sourceMappingURL=format.js.map